import { useState } from 'react';
import { Send, MessageSquare, User, Clock, TrendingUp, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { getPlatformConfig } from '../../../constants/platformLogos';

const ResponseDeploymentCard = ({ 
  response, 
  isSelected, 
  onSelect, 
  onPublishNow,
  connectedAccounts,
  engagementPrediction 
}) => {
  const [showFullResponse, setShowFullResponse] = useState(false);

  const platform = response?.comment?.platform;
  const platformConfig = getPlatformConfig(platform);
  const finalResponse = response?.finalResponse || response?.suggestedResponse;

  const formatResponseForPlatform = (text, targetPlatform) => {
    const config = getPlatformConfig(targetPlatform);
    if (!config) return text;

    // Truncate if exceeds character limit
    if (text?.length > config?.charLimit) {
      return text?.substring(0, config?.charLimit - 3) + '...';
    }

    return text;
  };

  const getFormattingStatus = (text, targetPlatform) => {
    const config = getPlatformConfig(targetPlatform);
    if (!config) return 'unknown';

    const length = text?.length;
    const limit = config?.charLimit;

    if (length <= limit * 0.8) return 'optimal';
    if (length <= limit) return 'good';
    return 'needs-truncation';
  };

  const getStatusColor = (status) => {
    const colors = {
      optimal: 'bg-green-100 text-green-800 border-green-300',
      good: 'bg-blue-100 text-blue-800 border-blue-300',
      'needs-truncation': 'bg-orange-100 text-orange-800 border-orange-300'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      optimal: 'Optimal Length',
      good: 'Good Length',
      'needs-truncation': 'Will Truncate'
    };
    return labels?.[status] || 'Unknown';
  };

  const formattingStatus = getFormattingStatus(finalResponse, platform);
  const formattedResponse = formatResponseForPlatform(finalResponse, platform);

  return (
    <div className={`bg-white rounded-lg border-2 shadow-sm transition-all ${
      isSelected ? 'border-blue-500 shadow-md' : 'border-gray-200'
    }`}>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3 flex-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelect}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {platformConfig && (
                  <img 
                    src={platformConfig?.logo} 
                    alt={platformConfig?.alt}
                    className="w-5 h-5 object-contain"
                  />
                )}
                <span className="font-semibold text-gray-900">{platformConfig?.name || platform}</span>
                <span className="text-sm text-gray-500">•</span>
                <span className="text-sm text-gray-600">
                  {new Date(response?.createdAt)?.toLocaleDateString()}
                </span>
              </div>

              {/* Original Comment Context */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2 mb-2">
                  <User className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {response?.comment?.commenterName || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      {response?.comment?.commentText}
                    </p>
                  </div>
                </div>
                {response?.comment?.sentiment && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      response?.comment?.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                      response?.comment?.sentiment === 'negative'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {response?.comment?.sentiment}
                    </span>
                    {response?.comment?.intent && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {response?.comment?.intent}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Engagement Prediction */}
          <div className="ml-3 text-right">
            <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span>Predicted Engagement</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{engagementPrediction}%</p>
          </div>
        </div>

        {/* AI Response Preview */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">AI-Generated Response</span>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(formattingStatus)}`}>
              {getStatusLabel(formattingStatus)}
            </span>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-gray-900 whitespace-pre-wrap">
              {showFullResponse ? finalResponse : (
                finalResponse?.length > 200 
                  ? finalResponse?.substring(0, 200) + '...' 
                  : finalResponse
              )}
            </p>
            {finalResponse?.length > 200 && (
              <button
                onClick={() => setShowFullResponse(!showFullResponse)}
                className="text-sm text-blue-600 hover:text-blue-700 mt-2 font-medium"
              >
                {showFullResponse ? 'Show Less' : 'Show More'}
              </button>
            )}
          </div>

          {/* Character Count */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
            <span>
              {finalResponse?.length} / {platformConfig?.charLimit} characters
            </span>
            {formattingStatus === 'needs-truncation' && (
              <span className="text-orange-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Will be truncated to fit platform limit
              </span>
            )}
          </div>
        </div>

        {/* Platform-Specific Formatting Preview */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-900 mb-2">Platform Formatting Preview</p>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-start gap-2">
              {platformConfig && (
                <img 
                  src={platformConfig?.logo} 
                  alt={platformConfig?.alt}
                  className="w-4 h-4 object-contain mt-0.5"
                />
              )}
              <div className="flex-1">
                <p className="text-sm text-gray-700">{formattedResponse}</p>
                {formattedResponse !== finalResponse && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Response truncated to meet {platformConfig?.name} character limit
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Safety & Quality Indicators */}
        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              response?.safetyScore >= 80 ? 'bg-green-500' :
              response?.safetyScore >= 60 ? 'bg-yellow-500': 'bg-red-500'
            }`}></div>
            <span className="text-sm text-gray-600">
              Safety Score: <span className="font-medium text-gray-900">{response?.safetyScore || 'N/A'}</span>
            </span>
          </div>

          {response?.approvedAt && (
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-600">
                Approved {new Date(response?.approvedAt)?.toLocaleDateString()}
              </span>
            </div>
          )}

          {response?.safetyFlags?.length > 0 && (
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-600" />
              <span className="text-sm text-orange-600">
                {response?.safetyFlags?.length} flag{response?.safetyFlags?.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              Ready for immediate publishing
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={onPublishNow}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Send className="w-4 h-4 mr-1" />
              Publish Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseDeploymentCard;